// src/api/distributionService.ts
import axios from 'axios';
import { 
  DistributionLocation, 
  GasPurchase, 
  GasSale, 
  CashStocking, 
  CashDispense, 
  Customer,
  LocationBalance,
  ProfitReport,
  SalesReport,
  PurchasesReport
} from '../models/distribution';

const API_URL = process.env.REACT_APP_API_URL;

// Configure axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {

    const storedUser = localStorage.getItem('user');
    if(storedUser) {
          const user = JSON.parse(storedUser);
  if (user) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
    }

  return config;
});

export const getAllLocations = async (): Promise<DistributionLocation[]> => {
  const response = await api.get('/distribution/locations');
  return response.data;
};

export const createDistributionLocation = async (name: string, address: string): Promise<DistributionLocation> => {
  const response = await api.post('/distribution/locations', { name, address });
  return response.data;
};

export const recordGasPurchase = async (data: {
  locationId: string;
  quantityKg: number;
  pricePerKg: number;
  transactionDate: string;
}): Promise<GasPurchase> => {
  const response = await api.post('/distribution/purchases', data);
  return response.data;
};

export const recordGasSale = async (data: {
  locationId: string;
  customerId?: string;
  purchaseId?: string;
  quantityKg: number;
  pricePerKg: number;
  paymentChannel: string;
  transactionDate?: string;
}): Promise<GasSale> => {
  const response = await api.post('/distribution/sales', data);
  return response.data;
};

export const recordPOSTransaction = async (data: {
  locationId: string;
  customerId?: string;
  transactionType: string;
  amount: number;
  charges: number;
    transactionDate?: string;

}): Promise<GasSale> => {
  const response = await api.post('/distribution/recordpostransaction', data);
  return response.data;
};

export const recordCashStocking = async (data: {
  locationId: string;
  amount: number;
  source: string;
  reference: string;
}): Promise<CashStocking> => {
  const response = await api.post('/distribution/cash/stocking', data);
  return response.data;
};

export const recordCashDispense = async (data: {
  locationId: string;
  amount: number;
  customerId?: string;
  source: string;
}): Promise<CashDispense> => {
  const response = await api.post('/distribution/cash/dispense', data);
  return response.data;
};

export const createCustomer = async (data: {
  name: string;
  phone: string;
  email?: string;
}): Promise<Customer> => {
  const response = await api.post('/distribution/customers', data);
  return response.data;
};

export const getLocationBalance = async (locationId: string): Promise<LocationBalance> => {
  const response = await api.get(`/distribution/balance/${locationId}`);
  return response.data;
};

export const getProfitReport = async (
  locationId: string,
  startDate: Date,
  endDate: Date
): Promise<ProfitReport> => {
  const response = await api.get(`/distribution/profit/${locationId}`, {
    params: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  });
  return response.data;
};

export const getGasPurchases = async (locationId: string): Promise<GasPurchase[]> => {
  const response = await api.get(`/distribution/getpurchase`, {
    params: {
      locationId
    }
  });
  return response.data.data;
};

export const getGasSales = async (locationId: string): Promise<GasSale[]> => {
  const response = await api.get(`/distribution/sales/${locationId}`);
  return response.data;
};

export const getCashTransactions = async (locationId: string): Promise<(CashStocking | CashDispense)[]> => {
  const response = await api.get(`/distribution/cash/transactions/${locationId}`);
  return response.data;
};

export const getCustomers = async (locationId:string): Promise<Customer[]> => {
  const response = await api.get('/distribution/customers/get', {
    params: {
      locationId
    }
  });
  return response.data.data;
};

export const getSalesReport = async (
  locationId: string,
  startDate: Date,
  endDate: Date
): Promise<SalesReport> => {
  const response = await api.get(`/distribution/getsales`, {
    params: {
      locationId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  });
  return response.data;
};

export const getPurchasesReport = async (
  locationId: string,
  startDate: Date,
  endDate: Date
): Promise<PurchasesReport> => {
  const response = await api.get(`/distribution/getpurchase`, {
    params: {
      locationId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  });
  return response.data;
};

export const getPOSReport = async (
  locationId: string,
  startDate: Date,
  endDate: Date
): Promise<any> => {
  const response = await api.get(`/distribution/getpostransactions`, {
    params: {
      locationId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  });
  return response.data;
};